// src/utils/verificheUtils.js
// Unico posto dove vive la logica delle verifiche periodiche obbligatorie (DPR 462/01 e
// normative correlate): calcolo della prossima scadenza, stato, e giorni residui.
// Riusata dalla vista aggregata, dalla scheda impianto e dai riepiloghi, così i tre punti
// non possono mai mostrare date o stati diversi per la stessa verifica.
import { toLocalDateStr } from "./dateUtils";

// Tipologie di partenza con la loro periodicità di legge — sono suggerimenti precompilati,
// non un vincolo: la periodicità resta sempre modificabile a mano sulla singola scheda,
// perché cambia con l'ambiente (luoghi ordinari, cantieri, ambienti a maggior rischio) e
// può cambiare con la normativa.
export const TIPOLOGIE_VERIFICA = [
  { nome:"Impianto di terra — luoghi ordinari",           mesi:60, norma:"DPR 462/01 art. 4" },
  { nome:"Impianto di terra — ambienti a maggior rischio", mesi:24, norma:"DPR 462/01 art. 4" },
  { nome:"Impianto di terra — cantieri",                   mesi:24, norma:"DPR 462/01 art. 4" },
  { nome:"Impianto di terra — locali medici",              mesi:24, norma:"DPR 462/01 art. 4" },
  { nome:"Protezione scariche atmosferiche — luoghi ordinari", mesi:60, norma:"DPR 462/01 art. 6" },
  { nome:"Protezione scariche atmosferiche — maggior rischio", mesi:24, norma:"DPR 462/01 art. 6" },
  { nome:"Impianti in luoghi con pericolo di esplosione",  mesi:24, norma:"DPR 462/01 art. 8" },
  { nome:"Verifica cabina MT/BT",                          mesi:12, norma:"CEI 78-17 (raccomandata)" },
  { nome:"Altro",                                          mesi:12, norma:"" },
];

export const STATI_VERIFICA = {
  programmata:      { label:"Programmata",       colore:"#6b7280", bg:"#f3f4f6", automatico:true },
  in_avviso:        { label:"In avviso",         colore:"#d97706", bg:"#fff7ed", automatico:true },
  in_pianificazione:{ label:"In pianificazione", colore:"#2563eb", bg:"#eff6ff", automatico:false },
  proposta_cliente: { label:"Proposta al cliente",colore:"#7c3aed", bg:"#ede9fe", automatico:false },
  scaduta:          { label:"Scaduta",           colore:"#dc2626", bg:"#fef2f2", automatico:true },
  completata:       { label:"Completata",        colore:"#059669", bg:"#d1fae5", automatico:false },
};

// Soglie di preavviso di default — parametrizzabili dall'organizzazione, mai fisse qui.
export const SOGLIE_DEFAULT = [60, 30, 15];

// Somma mesi a una data restando sulla stessa "posizione" del mese: 15 marzo + 24 mesi =
// 15 marzo di due anni dopo. Se il giorno non esiste nel mese di arrivo (31 gennaio + 1
// mese) si prende l'ultimo giorno valido, invece di scivolare al mese successivo.
export function aggiungiMesi(dataStr, mesi) {
  if (!dataStr || !mesi) return null;
  const d = new Date(dataStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const giornoOriginale = d.getDate();
  d.setMonth(d.getMonth() + Number(mesi));
  if (d.getDate() !== giornoOriginale) d.setDate(0); // rientra all'ultimo giorno del mese giusto
  return toLocalDateStr(d);
}

export function calcolaProssimaScadenza(scheda) {
  if (!scheda?.dataUltimaVerifica || !scheda?.periodicitaMesi) return null;
  return aggiungiMesi(scheda.dataUltimaVerifica, scheda.periodicitaMesi);
}

export function giorniAllaScadenza(dataScadenza) {
  if (!dataScadenza) return null;
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const d = new Date(dataScadenza + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return Math.round((d - oggi) / 86400000);
}

// Lo stato è in parte calcolato e in parte deciso da una persona.
// Calcolati: programmata / in_avviso / scaduta — dipendono solo dalla data, e ricalcolarli
// ogni volta evita che restino "congelati" a un valore vecchio.
// Manuali: in_pianificazione / proposta_cliente / completata — sono decisioni, e vincono
// sul calcolo automatico finché la scadenza non passa. Fa eccezione "scaduta": se la data
// è superata deve emergere comunque, anche se qualcuno aveva segnato "in pianificazione",
// altrimenti una verifica dimenticata resterebbe nascosta dietro uno stato rassicurante.
export function statoVerifica(scheda, sogliaMaxGiorni = 60) {
  const scadenza = scheda.dataProssimaScadenza || calcolaProssimaScadenza(scheda);
  if (!scadenza) return "programmata";
  const g = giorniAllaScadenza(scadenza);
  if (g !== null && g < 0) return "scaduta";
  if (scheda.statoManuale && STATI_VERIFICA[scheda.statoManuale] && !STATI_VERIFICA[scheda.statoManuale].automatico) {
    return scheda.statoManuale;
  }
  if (g !== null && g <= sogliaMaxGiorni) return "in_avviso";
  return "programmata";
}

// Testo leggibile dei giorni residui — "tra 45 giorni", "oggi", "scaduta da 12 giorni".
export function testoScadenza(dataScadenza) {
  const g = giorniAllaScadenza(dataScadenza);
  if (g === null) return "—";
  if (g === 0) return "scade oggi";
  if (g > 0) return `tra ${g} giorn${g===1?"o":"i"}`;
  const r = Math.abs(g);
  return `scaduta da ${r} giorn${r===1?"o":"i"}`;
}

export function fmtDataIt(dataStr) {
  if (!dataStr) return "—";
  const d = new Date(dataStr + "T00:00:00");
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("it-IT");
}
